import { useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  CheckCircle2,
  Download,
  Eye,
  FileText,
  Image as ImageIcon,
  Inbox,
  Loader2,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import type { Referral } from '@/hooks/useReferrals';
import {
  useArchiveDocument,
  useAssignReferralDocument,
  useGetDocumentDownloadUrl,
  useReferralDocuments,
  useUploadDocument,
  type DocumentRow,
} from '@/hooks/useDocuments';
import {
  FilePreviewOverlay,
  type FilePreviewSource,
} from '@/components/documents/FilePreviewOverlay';
import {
  findSlot,
  getRequestedFileSlots,
  type ReferralFileSlot,
} from '@/components/referrals/referralFileSlots';

interface ReferralFilesTabProps {
  referral: Referral;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType: string) {
  if (mimeType.startsWith('image/')) return ImageIcon;
  return FileText;
}

export function ReferralFilesTab({ referral }: ReferralFilesTabProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<FilePreviewSource | null>(null);
  const [dragOverInbox, setDragOverInbox] = useState(false);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [draggingDocId, setDraggingDocId] = useState<string | null>(null);

  const { data: documents = [], isLoading, refetch } = useReferralDocuments(referral.id);
  const uploadDocument = useUploadDocument();
  const assignDocument = useAssignReferralDocument();
  const archiveDocument = useArchiveDocument();
  const getDownloadUrl = useGetDocumentDownloadUrl();

  const requestedSlots = useMemo(() => getRequestedFileSlots(referral), [referral]);

  const docsBySlot = useMemo(() => {
    const map = new Map<string, DocumentRow[]>();
    for (const doc of documents) {
      if (!doc.referral_category || !doc.referral_field_name) continue;
      const key = `${doc.referral_category}:${doc.referral_field_name}`;
      const list = map.get(key) ?? [];
      list.push(doc);
      map.set(key, list);
    }
    return map;
  }, [documents]);

  const inboxDocs = useMemo(
    () => documents.filter((d) => !d.referral_category || !d.referral_field_name),
    [documents],
  );

  const missingSlots = useMemo(
    () => requestedSlots.filter((s) => (docsBySlot.get(s.key)?.length ?? 0) === 0),
    [requestedSlots, docsBySlot],
  );

  const doneCount = requestedSlots.length - missingSlots.length;

  async function uploadFiles(files: FileList | File[], assignTo?: ReferralFileSlot) {
    const list = Array.from(files);
    if (list.length === 0) return;

    setUploadingCount(list.length);
    let ok = 0;
    let failed = 0;

    for (const file of list) {
      try {
        await uploadDocument.mutateAsync({
          clinicId: referral.clinic_id,
          patientId: referral.patient_id ?? null,
          referralId: referral.id,
          referralCategory: assignTo?.category ?? null,
          referralFieldName: assignTo?.fieldName ?? null,
          file,
          tags: assignTo
            ? [`Referral - ${assignTo.category}`, assignTo.fieldName]
            : ['Referral - inbox'],
        });
        ok += 1;
      } catch (err) {
        console.error('Upload failed:', err);
        failed += 1;
      } finally {
        setUploadingCount((c) => Math.max(0, c - 1));
      }
    }

    await refetch();
    if (ok > 0) toast.success(t('referrals.files.upload_success_count', { count: ok }));
    if (failed > 0) toast.error(t('referrals.files.upload_failed_count', { count: failed }));
  }

  async function handleAssign(doc: DocumentRow, slot: ReferralFileSlot | null) {
    try {
      await assignDocument.mutateAsync({
        documentId: doc.id,
        referralCategory: slot?.category ?? null,
        referralFieldName: slot?.fieldName ?? null,
      });
      toast.success(
        slot
          ? t('referrals.files.assigned_to', { label: slot.label })
          : t('referrals.files.moved_to_inbox'),
      );
      await refetch();
    } catch (err) {
      console.error('Assign failed:', err);
      toast.error(t('referrals.files.assign_failed'));
    }
  }

  async function handleRemove(doc: DocumentRow) {
    if (!confirm(t('journal.attachments.remove'))) return;
    try {
      await archiveDocument.mutateAsync(doc.id);
      toast.success(t('journal.attachments.removed'));
      await refetch();
    } catch (err) {
      console.error('Remove failed:', err);
      toast.error(t('journal.attachments.errors.remove_failed'));
    }
  }

  async function handleDownload(doc: DocumentRow) {
    try {
      const url = await getDownloadUrl.mutateAsync(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('documents.download_failed'));
    }
  }

  function handleView(doc: DocumentRow) {
    setPreview({
      type: 'document',
      documentId: doc.id,
      filename: doc.original_filename,
      mimeType: doc.mime_type,
    });
  }

  function onDragStartDoc(e: DragEvent, doc: DocumentRow) {
    setDraggingDocId(doc.id);
    e.dataTransfer.setData('text/plain', doc.id);
    e.dataTransfer.effectAllowed = 'move';
  }

  function onDragEndDoc() {
    setDraggingDocId(null);
    setDropTargetKey(null);
  }

  async function onDropOnSlot(e: DragEvent, slot: ReferralFileSlot) {
    e.preventDefault();
    setDropTargetKey(null);
    const docId = e.dataTransfer.getData('text/plain');
    const doc = documents.find((d) => d.id === docId);
    if (doc) {
      await handleAssign(doc, slot);
      return;
    }
    if (e.dataTransfer.files?.length) {
      await uploadFiles(e.dataTransfer.files, slot);
    }
  }

  async function onDropInbox(e: DragEvent) {
    e.preventDefault();
    setDragOverInbox(false);
    const docId = e.dataTransfer.getData('text/plain');
    const doc = documents.find((d) => d.id === docId);
    if (doc) {
      await handleAssign(doc, null);
      return;
    }
    if (e.dataTransfer.files?.length) {
      await uploadFiles(e.dataTransfer.files);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-ds-muted" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {preview && (
        <FilePreviewOverlay source={preview} onClose={() => setPreview(null)} />
      )}

      {/* Progress summary */}
      <section className="rounded-lg border border-ds-line bg-ds-card p-5 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-medium text-ds-text">
              {t('referrals.files.title')}
            </h2>
            <p className="mt-1 text-sm text-ds-muted">
              {t('referrals.files.subtitle')}
            </p>
          </div>
          <div className="text-sm font-medium text-ds-text">
            {requestedSlots.length === 0
              ? t('referrals.files.no_requested')
              : t('referrals.files.progress', {
                  done: doneCount,
                  total: requestedSlots.length,
                })}
          </div>
        </div>
      </section>

      {/* Bulk inbox */}
      <section className="rounded-lg border border-ds-line bg-ds-card p-5 shadow-card">
        <div className="mb-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-medium text-ds-text">
              {t('referrals.files.inbox_title')}
            </h3>
            <p className="mt-0.5 text-sm text-ds-muted">
              {t('referrals.files.inbox_hint')}
            </p>
          </div>
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingCount > 0}
          >
            {uploadingCount > 0 ? <Loader2 className="animate-spin" /> : <Upload />}
            {t('referrals.files.bulk_upload')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.docx,.xlsx,.pptx,.txt"
            onChange={(e) => {
              if (e.target.files) void uploadFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOverInbox(true);
          }}
          onDragLeave={() => setDragOverInbox(false)}
          onDrop={(e) => void onDropInbox(e)}
          className={`rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ${
            dragOverInbox
              ? 'border-ds-teal bg-ds-teal-tint  '
              : 'border-ds-field '
          }`}
        >
          <Inbox className="mx-auto h-8 w-8 text-ds-muted" />
          <p className="mt-2 text-sm text-ds-muted">
            {t('referrals.files.drop_here')}
          </p>
        </div>

        {inboxDocs.length > 0 && (
          <div className="mt-4 space-y-2">
            {inboxDocs.map((doc) => (
              <FileRow
                key={doc.id}
                doc={doc}
                slots={requestedSlots}
                dragging={draggingDocId === doc.id}
                onDragStart={onDragStartDoc}
                onDragEnd={onDragEndDoc}
                onView={handleView}
                onDownload={(d) => void handleDownload(d)}
                onRemove={(d) => void handleRemove(d)}
                onAssign={(d, slot) => void handleAssign(d, slot)}
                assignLabel={t('referrals.files.assign_to')}
                unassignLabel={t('referrals.files.unassigned_option')}
              />
            ))}
          </div>
        )}
      </section>

      {/* Missing files */}
      <section className="rounded-lg border border-ds-line bg-ds-card p-5 shadow-card">
        <h3 className="text-base font-medium text-ds-text">
          {t('referrals.files.missing_title')}
        </h3>
        <p className="mt-0.5 mb-4 text-sm text-ds-muted">
          {t('referrals.files.missing_hint')}
        </p>

        {requestedSlots.length === 0 ? (
          <p className="text-sm text-ds-muted">
            {t('referrals.files.no_requested')}
          </p>
        ) : missingSlots.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-ds-green-ink">
            <CheckCircle2 className="h-4 w-4" />
            {t('referrals.files.all_done')}
          </div>
        ) : (
          <div className="space-y-2">
            {missingSlots.map((slot) => (
              <SlotDropRow
                key={slot.key}
                slot={slot}
                status="missing"
                active={dropTargetKey === slot.key}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDropTargetKey(slot.key);
                }}
                onDragLeave={() => setDropTargetKey(null)}
                onDrop={(e) => void onDropOnSlot(e, slot)}
                onQuickUpload={(files) => void uploadFiles(files, slot)}
                uploadLabel={t('documents.upload')}
              />
            ))}
          </div>
        )}
      </section>

      {/* All assigned files by group */}
      <section className="rounded-lg border border-ds-line bg-ds-card p-5 shadow-card">
        <h3 className="text-base font-medium text-ds-text mb-4">
          {t('referrals.files.all_title')}
        </h3>

        {documents.filter((d) => d.referral_category && d.referral_field_name).length === 0 ? (
          <p className="text-sm text-ds-muted">
            {t('referrals.files.no_assigned')}
          </p>
        ) : (
          <div className="space-y-6">
            {groupAssigned(documents).map(([group, items]) => (
              <div key={group}>
                <h4 className="mb-2 text-sm font-medium text-ds-text">
                  {group}
                </h4>
                <div className="space-y-2">
                  {items.map((doc) => {
                    const slot = findSlot(doc.referral_category, doc.referral_field_name);
                    return (
                      <FileRow
                        key={doc.id}
                        doc={doc}
                        slots={requestedSlots}
                        slotLabel={slot?.label}
                        dragging={draggingDocId === doc.id}
                        onDragStart={onDragStartDoc}
                        onDragEnd={onDragEndDoc}
                        onView={handleView}
                        onDownload={(d) => void handleDownload(d)}
                        onRemove={(d) => void handleRemove(d)}
                        onAssign={(d, s) => void handleAssign(d, s)}
                        assignLabel={t('referrals.files.assign_to')}
                        unassignLabel={t('referrals.files.unassigned_option')}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Drop zones for requested slots that already have files (reassign targets) */}
        {requestedSlots.length > 0 && (
          <div className="mt-6 border-t border-ds-line pt-4">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-ds-muted">
              {t('referrals.files.drop_targets')}
            </p>
            <div className="grid gap-2 sm:grid-cols-2">
              {requestedSlots.map((slot) => {
                const count = docsBySlot.get(slot.key)?.length ?? 0;
                return (
                  <SlotDropRow
                    key={`target-${slot.key}`}
                    slot={slot}
                    status={count > 0 ? 'done' : 'missing'}
                    fileCount={count}
                    active={dropTargetKey === slot.key}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setDropTargetKey(slot.key);
                    }}
                    onDragLeave={() => setDropTargetKey(null)}
                    onDrop={(e) => void onDropOnSlot(e, slot)}
                    onQuickUpload={(files) => void uploadFiles(files, slot)}
                    uploadLabel={t('documents.upload')}
                    compact
                  />
                );
              })}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

function groupAssigned(documents: DocumentRow[]): [string, DocumentRow[]][] {
  const map = new Map<string, DocumentRow[]>();
  for (const doc of documents) {
    if (!doc.referral_category || !doc.referral_field_name) continue;
    const slot = findSlot(doc.referral_category, doc.referral_field_name);
    const group = slot?.group ?? doc.referral_category;
    const list = map.get(group) ?? [];
    list.push(doc);
    map.set(group, list);
  }
  return Array.from(map.entries());
}

function FileRow({
  doc,
  slots,
  slotLabel,
  dragging,
  onDragStart,
  onDragEnd,
  onView,
  onDownload,
  onRemove,
  onAssign,
  assignLabel,
  unassignLabel,
}: {
  doc: DocumentRow;
  slots: ReferralFileSlot[];
  slotLabel?: string;
  dragging: boolean;
  onDragStart: (e: DragEvent, doc: DocumentRow) => void;
  onDragEnd: () => void;
  onView: (doc: DocumentRow) => void;
  onDownload: (doc: DocumentRow) => void;
  onRemove: (doc: DocumentRow) => void;
  onAssign: (doc: DocumentRow, slot: ReferralFileSlot | null) => void;
  assignLabel: string;
  unassignLabel: string;
}) {
  const { t } = useTranslation();
  const Icon = fileIcon(doc.mime_type);
  const currentKey =
    doc.referral_category && doc.referral_field_name
      ? `${doc.referral_category}:${doc.referral_field_name}`
      : '';

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, doc)}
      onDragEnd={onDragEnd}
      className={`flex flex-wrap items-center gap-2 rounded-lg border border-ds-line bg-ds-sunk px-3 py-2   ${
        dragging ? 'opacity-50' : ''
      }`}
    >
      <Icon className="h-4 w-4 shrink-0 text-ds-muted" />
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => onView(doc)}
          className="block w-full truncate text-left text-sm text-ds-text hover:text-ds-teal"
        >
          {doc.original_filename}
        </button>
        <p className="text-[11px] text-ds-muted">
          {formatFileSize(doc.size_bytes)}
          {slotLabel ? ` · ${slotLabel}` : ''}
        </p>
      </div>

      <select
        value={currentKey}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) {
            onAssign(doc, null);
            return;
          }
          const slot = slots.find((s) => s.key === value) ?? null;
          onAssign(doc, slot);
        }}
        className="max-w-[200px] rounded border border-ds-field bg-ds-card px-2 py-1 text-xs"
        title={assignLabel}
      >
        <option value="">{unassignLabel}</option>
        {slots.map((s) => (
          <option key={s.key} value={s.key}>
            {s.group}: {s.label}
          </option>
        ))}
      </select>

      <Button type="button" variant="tertiary" size="icon" onClick={() => onView(doc)} aria-label={t('common.view', 'Vis')}>
        <Eye />
      </Button>
      <Button type="button" variant="tertiary" size="icon" onClick={() => onDownload(doc)} aria-label={t('common.download', 'Download')}>
        <Download />
      </Button>
      <Button type="button" variant="danger" size="icon" onClick={() => onRemove(doc)} aria-label={t('common.delete')}>
        <X />
      </Button>
    </div>
  );
}

function SlotDropRow({
  slot,
  status,
  fileCount,
  active,
  compact,
  onDragOver,
  onDragLeave,
  onDrop,
  onQuickUpload,
  uploadLabel,
}: {
  slot: ReferralFileSlot;
  status: 'missing' | 'done';
  fileCount?: number;
  active: boolean;
  compact?: boolean;
  onDragOver: (e: DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent) => void;
  onQuickUpload: (files: FileList) => void;
  uploadLabel: string;
}) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <div
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`flex items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
        active
          ? 'border-ds-teal bg-ds-teal-tint  '
          : status === 'missing'
            ? 'border-ds-amber-line bg-ds-amber-tint/60  '
            : 'border-ds-line bg-ds-sunk  '
      } ${compact ? 'py-1.5' : ''}`}
    >
      {status === 'missing' ? (
        <AlertCircle className="h-4 w-4 shrink-0 text-ds-amber" />
      ) : (
        <CheckCircle2 className="h-4 w-4 shrink-0 text-ds-green" />
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm text-ds-text">{slot.label}</p>
        <p className="text-[11px] text-ds-muted">
          {slot.group}
          {typeof fileCount === 'number' && fileCount > 0 ? ` · ${fileCount}` : ''}
        </p>
      </div>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.docx,.xlsx,.pptx,.txt"
        onChange={(e) => {
          if (e.target.files) onQuickUpload(e.target.files);
          e.target.value = '';
        }}
      />
      <Button type="button" variant="secondary" onClick={() => inputRef.current?.click()}>
        <Upload />
        {uploadLabel}
      </Button>
    </div>
  );
}
